import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { SsoErrorCode, SsoProvider } from '@gokrew/models/sso';
import { cn } from '@/lib/utils';
import { useSsoProviders } from '../sso';
import { ssoErrorMessage } from '../ssoMessages';
import styles from './SsoNotice.module.css';

export interface SsoNoticeProps {
  code: SsoErrorCode;
  provider: SsoProvider | null;
  email: string | null;
  className?: string;
}

/**
 * Why a Google or Microsoft sign-in did not go through, on the login page.
 * A tenant that blocks user consent also gets the admin consent link (S13)
 * to copy and send to its administrator.
 */
export function SsoNotice({
  code,
  provider,
  email,
  className,
}: SsoNoticeProps) {
  const { t } = useTranslation(TranslationNS.Sso);
  const { data } = useSsoProviders();
  const [copied, setCopied] = useState(false);
  const consentUrl =
    code === 'admin_consent_required' ? data?.microsoftAdminConsentUrl : null;

  const copy = async () => {
    if (!consentUrl || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={cn(styles.notice, className)} role="alert">
      <p>{ssoErrorMessage(t, code, provider, email)}</p>
      {consentUrl && (
        <div className={styles.consent}>
          <span className={styles.consentUrl}>{consentUrl}</span>
          <button type="button" className={styles.copy} onClick={copy}>
            {copied ? t('error.copied') : t('error.copy_link')}
          </button>
        </div>
      )}
    </div>
  );
}
